import { useEffect, useState } from "react";
import { Box, Button, MenuItem, TextField, Typography } from "@mui/material";
import { toast } from "react-toastify";
import { adminPost } from "./adminApi";
import { consoleColors, primaryButtonSx } from "./consoleTheme";

const emptyLayout = { header_html: "", footer_html: "" };

function ParamChips({ keys, onInsert }) {
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.7 }}>
      {keys.map((key) => (
        <Box
          key={key}
          component="button"
          type="button"
          onClick={() => onInsert(`{{${key}}}`)}
          sx={{
            border: `1px solid ${consoleColors.line}`,
            background: "#f8fafc",
            borderRadius: 5,
            px: 1,
            py: 0.45,
            font: "inherit",
            fontSize: 12.5,
            fontWeight: 650,
            cursor: "pointer",
            color: consoleColors.accent,
            "&:hover": { borderColor: consoleColors.accent, background: "#e8f6fd" },
          }}
        >
          {`{{${key}}}`}
        </Box>
      ))}
    </Box>
  );
}

export default function EmailTemplates() {
  const [layout, setLayout] = useState(emptyLayout);
  const [templates, setTemplates] = useState([]);
  const [placeholders, setPlaceholders] = useState(["owner", "store", "otp", "email", "login_url", "year"]);
  const [openId, setOpenId] = useState("");
  const [previewHtml, setPreviewHtml] = useState("");
  const [saving, setSaving] = useState("");

  const load = async () => {
    const result = await adminPost("COMM_LIST");
    if (result.status === "200") {
      setLayout(result.data?.layout || emptyLayout);
      setTemplates((result.data?.templates || []).filter((row) => row.channel === "email"));
      if (result.data?.placeholders) setPlaceholders(result.data.placeholders);
    } else toast.error(result?.status_message || "Could not load email templates");
  };

  useEffect(() => { load(); }, []);

  const saveLayout = async () => {
    setSaving("layout");
    try {
      const result = await adminPost("COMM_LAYOUT_SAVE", layout);
      if (result.status === "200") {
        toast.success(result.data?.message || "Header and footer saved");
        setLayout(result.data?.layout || layout);
      } else toast.error(result?.status_message || "Could not save layout");
    } finally {
      setSaving("");
    }
  };

  const saveTemplate = async (row) => {
    setSaving(row.template_id);
    try {
      const result = await adminPost("COMM_SAVE", { ...row, channel: "email" });
      if (result.status === "200") {
        toast.success(result.data?.message || "Template saved");
        setTemplates((prev) => prev.map((item) => (item.template_id === row.template_id ? { ...item, ...result.data.template } : item)));
      } else toast.error(result?.status_message || "Could not save template");
    } finally {
      setSaving("");
    }
  };

  const preview = async (row) => {
    const result = await adminPost("COMM_PREVIEW", {
      header_html: layout.header_html,
      footer_html: layout.footer_html,
      subject: row?.subject || "Cashi",
      body: row?.body || "<p>Sample body</p>",
      event: row?.event || "store_otp",
    });
    if (result.status === "200") setPreviewHtml(result.data?.html || "");
    else toast.error(result?.status_message || "Could not preview");
  };

  const setTpl = (id, key, value) => {
    setTemplates((prev) => prev.map((row) => (row.template_id === id ? { ...row, [key]: value } : row)));
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, display: "grid", gap: 2 }}>
      <Box sx={{ background: "#fff", border: `1px solid ${consoleColors.line}`, borderRadius: 2.5, p: { xs: 2, md: 3 }, display: "grid", gap: 1.4 }}>
        <Typography sx={{ fontWeight: 750 }}>Header and footer</Typography>
        <Typography sx={{ color: consoleColors.muted, fontSize: 13.5, lineHeight: 1.5 }}>
          These wrap every email. Use full HTML. Click a parameter to add it.
        </Typography>
        <ParamChips keys={placeholders} onInsert={(token) => setLayout((prev) => ({ ...prev, header_html: `${prev.header_html || ""}${token}` }))} />
        <TextField
          label="Header HTML"
          value={layout.header_html || ""}
          onChange={(event) => setLayout((prev) => ({ ...prev, header_html: event.target.value }))}
          multiline
          minRows={5}
        />
        <TextField
          label="Footer HTML"
          value={layout.footer_html || ""}
          onChange={(event) => setLayout((prev) => ({ ...prev, footer_html: event.target.value }))}
          multiline
          minRows={5}
        />
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Button variant="contained" sx={primaryButtonSx} disabled={saving === "layout"} onClick={saveLayout}>
            {saving === "layout" ? "Saving..." : "Save header and footer"}
          </Button>
          <Button sx={{ textTransform: "none" }} onClick={() => preview({ body: "<p>Hello {{owner}}, this is a preview of {{store}}.</p>", subject: "Cashi preview", event: "store_otp" })}>
            Preview layout
          </Button>
        </Box>
      </Box>

      {templates.map((row) => {
        const open = openId === row.template_id;
        return (
          <Box key={row.template_id} sx={{ background: "#fff", border: `1px solid ${consoleColors.line}`, borderRadius: 2.5, p: { xs: 2, md: 2.4 } }}>
            <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
              <Box sx={{ flex: 1, minWidth: 200 }}>
                <Typography sx={{ fontWeight: 750 }}>{row.name}</Typography>
                <Typography sx={{ fontSize: 13, color: consoleColors.muted }}>{row.event} · {String(row.status) === "1" ? "On" : "Off"}</Typography>
              </Box>
              <Button size="small" sx={{ textTransform: "none" }} onClick={() => setOpenId(open ? "" : row.template_id)}>
                {open ? "Close" : "Edit HTML"}
              </Button>
            </Box>
            {open ? (
              <Box sx={{ display: "grid", gap: 1.3, mt: 1.6 }}>
                <TextField label="Template name" value={row.name} onChange={(event) => setTpl(row.template_id, "name", event.target.value)} />
                <TextField label="Subject" value={row.subject || ""} onChange={(event) => setTpl(row.template_id, "subject", event.target.value)} helperText="Subject can use the same {{parameters}}" />
                <TextField select label="Send this email" value={row.status} onChange={(event) => setTpl(row.template_id, "status", event.target.value)}>
                  <MenuItem value="1">On</MenuItem>
                  <MenuItem value="0">Off</MenuItem>
                </TextField>
                <Typography sx={{ fontSize: 13, color: consoleColors.muted }}>Parameters</Typography>
                <ParamChips keys={placeholders} onInsert={(token) => setTpl(row.template_id, "body", `${row.body || ""}${token}`)} />
                <TextField
                  label="HTML body"
                  value={row.body || ""}
                  onChange={(event) => setTpl(row.template_id, "body", event.target.value)}
                  multiline
                  minRows={8}
                  helperText="Inner HTML only. Header and footer are added around this."
                />
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  <Button variant="contained" sx={primaryButtonSx} disabled={saving === row.template_id} onClick={() => saveTemplate(row)}>
                    {saving === row.template_id ? "Saving..." : "Save template"}
                  </Button>
                  <Button sx={{ textTransform: "none" }} onClick={() => preview(row)}>Preview</Button>
                </Box>
              </Box>
            ) : null}
          </Box>
        );
      })}

      {previewHtml ? (
        <Box sx={{ background: "#fff", border: `1px solid ${consoleColors.line}`, borderRadius: 2.5, overflow: "hidden" }}>
          <Box sx={{ px: 2, py: 1.2, borderBottom: `1px solid ${consoleColors.line}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography sx={{ fontWeight: 750 }}>Preview</Typography>
            <Button size="small" sx={{ textTransform: "none" }} onClick={() => setPreviewHtml("")}>Close</Button>
          </Box>
          <iframe title="Email preview" srcDoc={previewHtml} style={{ width: "100%", height: 520, border: 0, background: "#f3f6f9" }} />
        </Box>
      ) : null}
    </Box>
  );
}
